import styles from './IdentityVerification.module.css';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { QRCode } from 'react-qrcode-logo';
import { createIDVSession, getIDVStatus, getTenantDetails } from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { useModal } from '../../hooks/modalContext';
import {
  ArrowRight,
  ChevronLeft,
  ExternalLink,
  Info,
  Monitor,
  Smartphone,
  X,
} from 'lucide-react';
import { showToast } from '../../utils/showToast';
import { shouldShowIdvChangeRequestPolling } from '../../utils/idvStatus';
import { pushStorage } from '../../webPush/pushNotificationStorage';

const IdentityVerification = () => {
  const { auth, currentUser } = useAuth();
  const userId = currentUser?.user?.userId;
  const { setModal, modalState } = useModal();
  const [webEnrolmentUrl, setWebEnrolmentUrl] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const sessionResponse = await createIDVSession(auth?.data.token);
        if (sessionResponse?.data?.webEnrolmentUrl) {
          setWebEnrolmentUrl(sessionResponse.data.webEnrolmentUrl);
        }
      } catch (error) {
        console.error('Error fetching IDV session data:', error);
        showToast({
          message: 'Could not start identity verification. Please try again.',
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [auth?.data.token]);

  const handleOpenVerification = () => {
    if (!webEnrolmentUrl) return;
    window.open(webEnrolmentUrl, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const [res, tenantRes] = await Promise.all([
          getIDVStatus(auth?.data.token).catch(() => null),
          getTenantDetails(auth?.data.token).catch(() => null),
        ]);

        const tenant = tenantRes?.data?.tenant;
        const tenantIdv = tenant?.tenantIdv;
        const { status, tenantIdvStatus, isNewIdvRequired } = res?.data ?? {};

        const isApproved =
          tenant?.isIdentityVerified === 1 ||
          (tenant?.isIdentityVerified as unknown) === true ||
          tenantIdv?.status === 'approved' ||
          status === 'AUTO_APPROVED' ||
          status === 'MANUAL_APPROVED' ||
          status === 'approved' ||
          tenantIdvStatus === 'approved';

        if (isApproved) {
          if (userId != null) {
            pushStorage.setHasCompletedIdv(userId);
          }
          setModal('pending-screen', {
            idvStatus: 'success',
            tenantBuildingId: modalState?.tenantBuildingId,
            pendingIdvAuthHold: modalState?.pendingIdvAuthHold,
          });
          clearInterval(interval);
          return;
        }

        if (
          !modalState?.pendingIdvAuthHold &&
          shouldShowIdvChangeRequestPolling({ tenantIdvStatus, status, isNewIdvRequired })
        ) {
          setModal('idv-change-request', {
            tenantBuildingId: modalState?.tenantBuildingId,
            pendingIdvAuthHold: modalState?.pendingIdvAuthHold,
          });
          clearInterval(interval);
        } else if (
          tenantIdvStatus === 'declined' ||
          status === 'AUTO_REJECTED' ||
          status === 'MANUAL_REJECTED'
        ) {
          setModal('idv-declined', {
            tenantBuildingId: modalState?.tenantBuildingId,
            pendingIdvAuthHold: modalState?.pendingIdvAuthHold,
          });
          clearInterval(interval);
        }
      } catch (error) {
        console.error('Polling IDV status failed:', error);
      }
    }, 10000); // every 10 seconds

    return () => clearInterval(interval); // clean up on unmount
  }, [auth?.data.token, modalState?.tenantBuildingId, modalState?.pendingIdvAuthHold, setModal, userId]);

  const handleNextClick = async () => {
    try {
      setLoadingStatus(true);
      const [res, tenantRes] = await Promise.all([
        getIDVStatus(auth?.data.token).catch(() => null),
        getTenantDetails(auth?.data.token).catch(() => null),
      ]);

      const tenant = tenantRes?.data?.tenant;
      const tenantIdv = tenant?.tenantIdv;
      const { status, tenantIdvStatus } = res?.data ?? {};

      const isApproved =
        tenant?.isIdentityVerified === 1 ||
        (tenant?.isIdentityVerified as unknown) === true ||
        tenantIdv?.status === 'approved' ||
        status === 'AUTO_APPROVED' ||
        status === 'MANUAL_APPROVED' ||
        status === 'approved' ||
        tenantIdvStatus === 'approved';

      if (isApproved) {
        if (userId != null) {
          pushStorage.setHasCompletedIdv(userId);
        }
        setModal('pending-screen', {
          idvStatus: 'success',
          tenantBuildingId: modalState?.tenantBuildingId,
          pendingIdvAuthHold: modalState?.pendingIdvAuthHold,
        });
      } else {
        setModal('pending-screen', {
          idvStatus: 'pending',
          tenantBuildingId: modalState?.tenantBuildingId,
          pendingIdvAuthHold: modalState?.pendingIdvAuthHold,
        });
      }
    } catch (err) {
      console.error('Error checking IDV status on Next:', err);
      setModal('pending-screen', {
        idvStatus: 'pending',
        tenantBuildingId: modalState?.tenantBuildingId,
        pendingIdvAuthHold: modalState?.pendingIdvAuthHold,
      });
    } finally {
      setLoadingStatus(false);
    }
  };

  return (
    <motion.main
      className={`${styles.main} `}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className={styles.headingOuter}>
        <div className='flex items-center gap-[12px]'>
          <button
            onClick={() =>
              setModal(null, {
                refreshProperties: true,
                refreshProfile: true,
              })
            }
          >
            <ChevronLeft stroke='#0E0E0B' strokeWidth={1.8} />
          </button>
          <div className={`${styles.heading}`}>Identity verification</div>
        </div>
        <X
          className={styles.crossIcon}
          size={18}
          onClick={() =>
            setModal(null, {
              refreshProperties: true,
              refreshProfile: true,
            })
          }
          stroke='#0E0E0B'
          strokeWidth={1.8}
        />
      </div>
      {loading ? (
        <div className={styles.sessionLoading} />
      ) : (
        <div className={styles.verifyContent}>
          <p className={styles.verifyIntro}>
            To protect your account and comply with financial regulations, we verify your identity
            through Plaid.
          </p>

          <div className={styles.optionCard}>
            <div className={styles.methodHeader}>
              <Smartphone size={18} strokeWidth={1.8} />
              <span className={styles.methodTitle}>Scan with your phone camera</span>
              <span className={styles.recommendedBadge}>Recommended</span>
            </div>
            {webEnrolmentUrl && (
              <div className={styles.qrOuter}>
                <QRCode value={webEnrolmentUrl} size={145} quietZone={8} ecLevel='M' />
              </div>
            )}
            <span className={styles.qrHelpText}>
              Point your smartphone camera at the QR code to open verification.
            </span>
          </div>

          <div className={styles.orDivider}>
            <span>or continue on this device</span>
          </div>

          <div className={styles.optionCard}>
            <div className={styles.methodHeader}>
              <Monitor size={18} strokeWidth={1.8} />
              <span className={styles.methodTitle}>Complete on this device</span>
            </div>
            <p className={styles.methodDescription}>
              Open verification in a new browser tab.
            </p>
            <button
              type='button'
              className={styles.openVerifyBtn}
              disabled={!webEnrolmentUrl || loadingStatus}
              onClick={handleOpenVerification}
            >
              <span>Open verification in new tab</span>
              <ExternalLink size={18} strokeWidth={1.8} />
            </button>
          </div>

          <div className={styles.autoDetectBox}>
            <Info size={18} className={styles.autoDetectIcon} />
            <span>
              Once you finish on Plaid, this screen will automatically detect your completion and
              advance. If it doesn’t update right away, tap below.
            </span>
          </div>
        </div>
      )}

      <button
        className={`${styles.submitBtn} ${(loading || loadingStatus || !webEnrolmentUrl) && styles.disableBtn}`}
        disabled={loading || loadingStatus || !webEnrolmentUrl}
        onClick={handleNextClick}
      >
        <span>I’ve completed verification</span>
        <ArrowRight stroke={loading || loadingStatus || !webEnrolmentUrl ? '#0E0E0B66' : '#fff'} />
      </button>
    </motion.main>
  );
};
export default IdentityVerification;
